import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Accordion } from 'src/design/molecules/Accordion';
import { Timeline } from 'src/design/molecules/Timeline';
import {
  IconArrowRight,
  IconCheck,
  IconCircleX,
  IconDots,
  IconEdit,
} from '@tabler/icons-react';
import React, { useMemo } from 'react';
import { formatDateTime } from 'src/utils';
import { ITimesheetHistoryEntry } from 'src/app/timesheets/types';

export interface ITimesheetHistoryPanelProps {
  history: ITimesheetHistoryEntry[];
  titleOrder?: 4 | 5;
  showEmptyState?: boolean;
}

const getTimelineIcon = (oldStatus?: string, newStatus?: string) => {
  if (newStatus?.toLowerCase().includes('approved')) {
    return <IconCheck size={16} />;
  }
  if (newStatus?.toLowerCase().includes('rejected')) {
    return <IconCircleX size={16} />;
  }
  if (newStatus?.toLowerCase().includes('draft')) {
    return <IconEdit size={16} />;
  }
  return <IconDots size={16} />;
};

const getTimelineColor = (oldStatus?: string, newStatus?: string) => {
  if (newStatus?.toLowerCase().includes('approved')) {
    return 'green';
  }
  if (newStatus?.toLowerCase().includes('rejected')) {
    return 'red';
  }
  if (newStatus?.toLowerCase().includes('draft')) {
    return 'gray';
  }
  return 'blue';
};

/** @registry Accordion-collapsed Mantine Timeline of a timesheet's status-change history, newest first, with per-status icon/color; hidden entirely when empty unless showEmptyState. */
export const TimesheetHistoryPanel: React.FC<ITimesheetHistoryPanelProps> = ({
  history,
  titleOrder = 4,
  showEmptyState = false,
}) => {
  const hasHistory = useMemo(() => history && history.length > 0, [history]);

  const sortedHistory = useMemo(
    () =>
      hasHistory
        ? [...history].sort((a, b) => {
            if (!a.createdAt || !b.createdAt) return 0;
            return (
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
          })
        : [],
    [history, hasHistory],
  );

  if (!hasHistory && !showEmptyState) {
    return null;
  }

  return (
    <Accordion defaultValue="history" variant="filled">
      <Accordion.Item value="history">
        <Accordion.Control>
          <Title order={titleOrder}>History</Title>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="md">
            {sortedHistory.length === 0 ? (
              <Text c="dimmed" size="sm">
                No history recorded.
              </Text>
            ) : (
              <Timeline
                active={sortedHistory.length}
                bulletSize={24}
                lineWidth={2}
              >
                {sortedHistory.map((item) => (
                  <Timeline.Item
                    key={item.id}
                    bullet={getTimelineIcon(item.oldStatus, item.newStatus)}
                    color={getTimelineColor(item.oldStatus, item.newStatus)}
                  >
                    <Stack gap="xs">
                      <Group c="bright" gap="xs">
                        {item.oldStatus && item.oldStatus !== item.newStatus ? (
                          <>
                            <Text c="bright" fw={700}>
                              {item.oldStatus}
                            </Text>
                            <IconArrowRight size={16} />
                            <Text c="bright" fw={700}>
                              {item.newStatus}
                            </Text>
                          </>
                        ) : (
                          <Text c="bright" fw={700}>
                            {item.newStatus}
                          </Text>
                        )}
                      </Group>

                      {item.message && (
                        <Text c="bright" size="sm">
                          {item.message}
                        </Text>
                      )}

                      <Text c="dimmed" size="xs">
                        {item.createdBy ? `${item.createdBy.fullName} • ` : ''}
                        {item.createdAt ? formatDateTime(item.createdAt) : ''}
                      </Text>
                    </Stack>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};
